'use client'

import { trackEvent } from '@patrianna/analytics'
import type { GameProduct } from '@patrianna/shared-patrianna-types/store/GameModule'
import dynamic from 'next/dynamic'
import { useSearchParams } from 'next/navigation'

import BuilderEventService from 'analyticActions/builder/events'
import { useAutoCloseSnackbarByTime } from 'hooks/useAutoCloseSnackbarByTime'
import { graphyteTrackGameClose, graphyteTrackGamePlay, trackGameEvent } from 'src/analyticActions'
import { LoaderFullscreen } from 'src/components/Loader'
import { IS_BRAND_PULSZ } from 'src/config/brands'
import { INTERNAL_GAME_NAVIGATION, removeDataFromSessionStorage } from 'src/utils/sessionStorage'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { getSnackbarMessageSelector } from 'store/modules/snackbar/selectors'
import type { SupportedCurrencies } from 'types/currencies'

import GameBackground from '../components/GameBackground'
import GameInitializationWrapper from '../GameInitializationWrapper'
import GameRedirectWrapper from '../GameRedirectWrapper'
import GameValidateWrapper from '../GameValidateWrapper'

type Props = {
  gameProduct: GameProduct
  currency: SupportedCurrencies
}

const BingoTypeGame = dynamic(() => import('../GameContainers/BingoTypeGame'), {
  loading: LoaderFullscreen,
  ssr: false,
})

const SlotTypeGame = dynamic(() => import('../GameContainers/SlotTypeGame'), {
  loading: LoaderFullscreen,
  ssr: false,
})

function SlotGame(props: Props) {
  const dispatch = useAppDispatch()
  const query = useSearchParams()
  const category = query.get('category')
  const gameIndex = query.get('gameIndex')
  const swimlaneIndex = query.get('swimlaneIndex')
  const refererPath = query.get('refererPath')
  const xPageIndex = query.get('xPageIndex')
  const yPageIndex = query.get('yPageIndex')
  const layout = query.get('layout')

  const onReadyHandler = (type: string) => {
    if (props.currency === 'SC') {
      BuilderEventService.trackSessionEvent('gameplay_per_session')
      dispatch(
        graphyteTrackGamePlay(props.gameProduct, {
          ...(category && { event_location: category }),
          ...(gameIndex && { event_location_index: Number(gameIndex) }),
        }),
      )
    }

    if (type === 'bingo') {
      trackEvent(`played_bingo_game_${props.currency.toLowerCase()}`, {
        category: props.gameProduct.title,
        label: props.gameProduct.code,
      })
    } else {
      trackEvent(`played_slot_game_${props.currency.toLowerCase()}`, {
        category: props.gameProduct.title,
        label: props.gameProduct.code,
      })
    }

    dispatch(
      trackGameEvent(
        'OPEN_GAME',
        props.gameProduct,
        props.currency,
        category,
        gameIndex,
        swimlaneIndex,
        refererPath,
        xPageIndex,
        yPageIndex,
        layout,
      ),
    )
  }

  const onCloseHandler = () => {
    if (props.currency === 'SC') {
      dispatch(graphyteTrackGameClose(props.gameProduct))
    }

    dispatch(
      trackGameEvent(
        'CLOSE_GAME',
        props.gameProduct,
        props.currency,
        category,
        gameIndex,
        swimlaneIndex,
        refererPath,
        xPageIndex,
        yPageIndex,
        layout,
      ),
    )
    removeDataFromSessionStorage(INTERNAL_GAME_NAVIGATION)
  }

  const currentSnackbarMessage = useAppSelector(getSnackbarMessageSelector)
  const isLevelUpSnackbar = currentSnackbarMessage?.toString().includes('level-up-message') ?? false

  useAutoCloseSnackbarByTime([currentSnackbarMessage?.toString() ?? ''], 3000, isLevelUpSnackbar && IS_BRAND_PULSZ)

  return (
    <GameRedirectWrapper currency={props.currency} loadingFallback={<GameBackground gameProduct={props.gameProduct} />}>
      <GameValidateWrapper gameProduct={props.gameProduct}>
        <GameInitializationWrapper gameProduct={props.gameProduct} currency={props.currency}>
          {(gameProduct, currency, tokenData) => {
            if (gameProduct.type === 'bingo') {
              return (
                <BingoTypeGame
                  tokenData={tokenData}
                  gameProduct={gameProduct}
                  currency={currency}
                  onReady={() => onReadyHandler(gameProduct.type)}
                  onCloseHandler={onCloseHandler}
                />
              )
            }

            return (
              <SlotTypeGame
                tokenData={tokenData}
                gameProduct={gameProduct}
                currency={currency}
                onReadyHandler={() => onReadyHandler(gameProduct.type)}
                onCloseHandler={onCloseHandler}
              />
            )
          }}
        </GameInitializationWrapper>
      </GameValidateWrapper>
    </GameRedirectWrapper>
  )
}

export default SlotGame
